"use client";

import { useState, useRef, useEffect } from "react";
import Image from "next/image";

type InfiniteCarouselProps = {
  showThumbnails?: boolean;
  altMiniThumbnails?: boolean;
  thumbnailCount?: number;
  images: Array<{ thumbnail?: string; heroImage: string, altText: string }>;
  insideButtons?: boolean;
  className?: string;
};

export function InfiniteCarousel({
  thumbnailCount = 2,
  showThumbnails = true,
  altMiniThumbnails = false,
  insideButtons = false,
  className,
  ...props
}: InfiniteCarouselProps) {
  const totalImages = props.images.length;
  // currentIndex: The index of the active item WITHIN the current visible thumbnail block (0 to thumbnailCount - 1)
  const [currentIndex, setCurrentIndex] = useState(0);
  // currentSlide: The absolute index of the first visible thumbnail (the offset)
  const [currentSlide, setCurrentSlide] = useState(0);

  const prevIndex = useRef(currentIndex);

  useEffect(() => {
    prevIndex.current = currentIndex;
  }, [currentIndex]);

  // Keep this for client-side rendering check, though often not needed in simple components
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // --- CORE LOGIC: ABSOLUTE INDEX CALCULATION ---
  // The absolute index in the entire images array is always:
  const getAbsoluteIndex = (localIndex: number, offset: number) => {
    return (offset + localIndex) % totalImages;
  };

  // --- HANDLERS ---

  const handleNextSlide = () => {
    const nextLocalIndex = currentIndex + 1;
    const nextAbsoluteIndex = (currentSlide + nextLocalIndex) % totalImages;

    if (nextLocalIndex < thumbnailCount) {
      // 1. Still within the current visible thumbnail block: just move the local index
      setCurrentIndex(nextLocalIndex);
    } else {
      // 2. Reached the end of the block: shift the block and reset the local index

      // The new start index is the next image's absolute index
      // which is currentSlide + thumbnailCount, but we use nextAbsoluteIndex
      // to ensure the next block starts correctly.
      setCurrentSlide(nextAbsoluteIndex);
      setCurrentIndex(0); // Reset local index to 0 for the new block
    }
  };

  const handlePreviousSlide = () => {
    const prevLocalIndex = currentIndex - 1;

    if (prevLocalIndex >= 0) {
      // 1. Still within the current visible thumbnail block: just move the local index
      setCurrentIndex(prevLocalIndex);
    } else {
      // 2. Reached the start of the block (moved from index 0): shift the block back and set local index to the end of the new block

      // Calculate the start of the previous block, handling wrap-around
      const prevBlockStart =
        (currentSlide - thumbnailCount + totalImages) % totalImages;

      setCurrentSlide(prevBlockStart);
      // Set the local index to the last position of the newly shifted block
      setCurrentIndex(thumbnailCount - 1);
    }
  };

  const handleThumbnailClick = (localIndex: number, absoluteIndex: number) => {
    // 1. Update the offset to make the clicked thumbnail the start of the block
    // This provides a smooth "scrolling" feel if you click a thumbnail far from the current active one.
    setCurrentSlide(absoluteIndex - localIndex);

    // 2. Set the local index to the clicked position
    setCurrentIndex(localIndex);
  };

  // --- RENDER ---

  if (!isMounted) {
    return (
      <div className="bg-bgColor lg:w-[39.375rem] lg:h-[23.4375rem] w-full min-h-[11.1875rem] rounded-3xl"></div>
    );
  }

  // Calculate the absolute index for the hero image (which uses the old formula)
  const heroImageAbsoluteIndex = getAbsoluteIndex(currentIndex, currentSlide);

  return (
    <div
      aria-roledescription="carousel"
      aria-label="Highlighted Cars"
      className={`${className} flex flex-col justify-center items-center`}
    >
      <div
        aria-live="polite"
        id="myCarousel-items"
        className={`${insideButtons ? "aspect-[522/600] sm:max-w-[522px] sm:max-h-[600px]" : "aspect-[522/522] sm:max-w-[522px] sm:max-h-[522px] "}   w-full h-full relative rounded-3xl `}
      >
        {insideButtons && (
          <span className="absolute z-10 bottom-5 flex w-full justify-center gap-x-3">
            <button
              aria-controls="myCarousel-items"
              aria-label="Previous Slide"
              className="cursor-pointer"
              onClick={handlePreviousSlide}
            >
              {/* SVG for Previous Button */}
              <svg
                width="30"
                height="30"
                viewBox="0 0 30 30"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  x="30"
                  y="30"
                  width="30"
                  height="30"
                  rx="15"
                  transform="rotate(-180 30 30)"
                  fill="#F5F5F7"
                />
                <path
                  d="M17 10L12 15L17 20"
                  stroke="#222429"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            <button
              aria-controls="myCarousel-items"
              aria-label="Next Slide"
              className="cursor-pointer"
              onClick={handleNextSlide}
            >
              {/* SVG for Next Button */}
              <svg
                width="30"
                height="30"
                viewBox="0 0 30 30"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect width="30" height="30" rx="15" fill="#F5F5F7" />
                <path
                  d="M13 20L18 15L13 10"
                  stroke="#222429"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </span>
        )}

        <Image
          key={heroImageAbsoluteIndex} // Key on the actual image index
          src={props.images[heroImageAbsoluteIndex]?.heroImage || ""}
          alt={props.images[heroImageAbsoluteIndex]?.altText || `Car Image ${heroImageAbsoluteIndex + 1} of ${totalImages}`}
          className={`rounded-3xl object-cover animate-horsepower-zoom`}
          fill
          loading="lazy"
        />
      </div>

      {/* do not show this div if !insideButtons */}
      {!insideButtons && (
        <div className="flex flex-row items-center mt-5 gap-5">
          <button
            aria-controls="myCarousel-items"
            aria-label="Previous Slide"
            className="cursor-pointer"
            onClick={handlePreviousSlide}
          >
            {/* SVG for Previous Button */}
            <svg
              width="30"
              height="30"
              viewBox="0 0 30 30"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect
                x="30"
                y="30"
                width="30"
                height="30"
                rx="15"
                transform="rotate(-180 30 30)"
                fill="#F5F5F7"
              />
              <path
                d="M17 10L12 15L17 20"
                stroke="#222429"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          {showThumbnails && (
            <div className="flex flex-row items-center gap-5">
              {new Array(thumbnailCount).fill(0).map((item, idx) => {
                // Calculate the correct absolute index for THIS thumbnail
                const thumbnailAbsoluteIndex = getAbsoluteIndex(
                  idx,
                  currentSlide
                );

                // Check if THIS thumbnail is the active one
                // It is active if its block offset is currentSlide AND its local index is currentIndex
                const isActive =
                  thumbnailAbsoluteIndex === heroImageAbsoluteIndex;

                return (
                  <button
                    type="button"
                    onClick={() =>
                      handleThumbnailClick(idx, thumbnailAbsoluteIndex)
                    }
                    className={`transition-all duration-200 focus:outline-none ${altMiniThumbnails
                      ? "lg:w-20 lg:h-20 w-10 h-10 rounded-md"
                      : "w-20 h-20 rounded-xl p-[.125rem]"
                      } overflow-hidden flex justify-center items-center ${isActive ? "border-2 border-black" : "border-none"
                      }`}
                    key={`image-${thumbnailAbsoluteIndex}`} // Key on the absolute index is better
                    aria-label={`View image ${thumbnailAbsoluteIndex + 1}`}
                    aria-current={isActive ? "true" : "false"}
                  >
                    {/* Thumbnail Image Logic */}
                    <Image
                      src={props.images[thumbnailAbsoluteIndex]?.thumbnail || ""}
                      width={altMiniThumbnails ? 41 : 165}
                      height={altMiniThumbnails ? 41 : 165}
                      fetchPriority="low"
                      loading="lazy"
                      className={`w-full h-full rounded-md object-cover`}
                      alt={props.images[thumbnailAbsoluteIndex]?.altText || `Thumbnail preview for image ${thumbnailAbsoluteIndex + 1
                        }`}
                    />
                  </button>
                );
              })}
            </div>
          )}

          <button
            aria-controls="myCarousel-items"
            aria-label="Next Slide"
            className="cursor-pointer"
            onClick={handleNextSlide}
          >
            {/* SVG for Next Button */}
            <svg
              width="30"
              height="30"
              viewBox="0 0 30 30"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect width="30" height="30" rx="15" fill="#F5F5F7" />
              <path
                d="M13 20L18 15L13 10"
                stroke="#222429"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      )}

    </div>
  );
}
